import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import type {
  PriceInput,
  WorkspaceCommand,
  WorkspaceOption,
  WorkspacePreview,
  WorkspacePromo,
  WorkspaceSnapshot,
  WorkspaceVariant,
} from "../../domain/contracts/live-workspace";
import {
  workspaceErrorMessage,
  workspaceRequest,
} from "../api/workspace-client";

const moneyFormatter = new Intl.NumberFormat("lo-LA");
export const kip = (value: number) => `${moneyFormatter.format(value)} ກີບ`;
export type OptionCandidate = {
  adminOptionDefinitionId: string;
  label: string;
  code: string;
  posDefaultSurchargeLak: number;
  hasRequiredFields: boolean;
};
export function WorkspaceSelect({
  label,
  value,
  onChange,
  items,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  items: Array<[string, string]>;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} className="min-h-11 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map(([value, name]) => (
            <SelectItem key={value} value={value}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
export function OptionEditor({
  candidates,
  value,
  onChange,
}: {
  candidates: OptionCandidate[];
  value: WorkspaceOption[];
  onChange: (value: WorkspaceOption[]) => void;
}) {
  const update = (
    candidate: OptionCandidate,
    patch: Partial<WorkspaceOption> | null,
  ) => {
    const other = value.filter(
      (o) => o.adminOptionDefinitionId !== candidate.adminOptionDefinitionId,
    );
    if (!patch) {
      onChange(other);
      return;
    }
    const current = value.find(
      (o) => o.adminOptionDefinitionId === candidate.adminOptionDefinitionId,
    );
    onChange([
      ...other,
      {
        adminOptionDefinitionId: candidate.adminOptionDefinitionId,
        label: candidate.label,
        surchargeLak: 0,
        isAutoAttach: true,
        requiresCustomerInput: candidate.hasRequiredFields,
        ...current,
        ...patch,
      },
    ]);
  };
  return (
    <div className="space-y-3">
      {candidates.map((candidate) => {
        const option = value.find(
          (o) =>
            o.adminOptionDefinitionId === candidate.adminOptionDefinitionId,
        );
        return (
          <div
            key={candidate.adminOptionDefinitionId}
            className="space-y-2 rounded-lg border p-3"
          >
            <WorkspaceSelect
              label={candidate.label}
              value={
                !option ? "off" : option.surchargeLak === 0 ? "free" : "extra"
              }
              items={[
                ["off", "ບໍ່ໃຊ້"],
                ["free", "ຟຣີ"],
                ["extra", "ຄິດເພີ່ມ"],
              ]}
              onChange={(mode) =>
                update(
                  candidate,
                  mode === "off"
                    ? null
                    : {
                        surchargeLak:
                          mode === "free"
                            ? 0
                            : Math.max(1000, candidate.posDefaultSurchargeLak),
                      },
                )
              }
            />
            {option && (
              <>
                <label
                  htmlFor={`workspace-option-price-${candidate.adminOptionDefinitionId}`}
                  className="block space-y-1"
                >
                  <span className="text-sm">ຄ່າບໍລິການ / ຊິ້ນ (ກີບ)</span>
                  <Input
                    id={`workspace-option-price-${candidate.adminOptionDefinitionId}`}
                    type="number"
                    min={0}
                    max={999999999}
                    value={option.surchargeLak}
                    className="min-h-11"
                    onChange={(event) =>
                      update(candidate, {
                        surchargeLak: Math.max(0, Number(event.target.value)),
                      })
                    }
                  />
                </label>
                <p className="text-muted-foreground text-sm">
                  {candidate.label}{" "}
                  {option.surchargeLak === 0 ? "ຟຣີ" : kip(option.surchargeLak)}{" "}
                  · {option.isAutoAttach ? "ລວມເຂົ້າອໍເດີ" : "ລູກຄ້າເລືອກເພີ່ມ"}
                  {option.requiresCustomerInput ? " · ຕ້ອງກອກຂໍ້ມູນ" : ""}
                </p>
                <details>
                  <summary className="min-h-11 cursor-pointer py-2 text-sm">
                    ລາຍລະອຽດເພີ່ມເຕີມ
                  </summary>
                  <div className="space-y-2">
                    <label
                      htmlFor={`workspace-option-auto-${candidate.adminOptionDefinitionId}`}
                      className="flex min-h-11 items-center gap-2"
                    >
                      <Checkbox
                        id={`workspace-option-auto-${candidate.adminOptionDefinitionId}`}
                        checked={option.isAutoAttach}
                        onCheckedChange={(checked) =>
                          update(candidate, { isAutoAttach: checked === true })
                        }
                      />
                      ແນບເຂົ້າອໍເດີອັດຕະໂນມັດ
                    </label>
                    <label
                      htmlFor={`workspace-option-required-${candidate.adminOptionDefinitionId}`}
                      className="flex min-h-11 items-center gap-2"
                    >
                      <Checkbox
                        id={`workspace-option-required-${candidate.adminOptionDefinitionId}`}
                        checked={option.requiresCustomerInput}
                        onCheckedChange={(checked) =>
                          update(candidate, {
                            requiresCustomerInput: checked === true,
                          })
                        }
                      />
                      ຕ້ອງກອກຊື່ / ເບີ
                    </label>
                  </div>
                </details>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function WorkspaceProductEditor({
  workspace,
  products,
  global = false,
  onClose,
  onSave,
  busy,
  saveError,
}: {
  workspace: WorkspaceSnapshot;
  products: WorkspaceVariant[];
  global?: boolean;
  onClose: () => void;
  onSave: (action: WorkspaceCommand["action"]) => Promise<boolean>;
  busy: boolean;
  saveError?: string | null;
}) {
  const [price, setPrice] = useState<PriceInput>({
    mode: global
      ? workspace.promo.listDiscountFixedLak != null
        ? "fixed"
        : "percent"
      : "sale",
    value: global
      ? (workspace.promo.listDiscountFixedLak ??
        workspace.promo.listDiscountPercent ??
        0)
      : products.length === 1
        ? products[0]!.salePrice
        : undefined,
  });
  const [changePrice, setChangePrice] = useState(true);
  const [codeEdit, setCodeEdit] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const singleProduct = products.length === 1 ? products[0] : undefined;
  const [quantity, setQuantity] = useState(
    singleProduct
      ? String(
          singleProduct.allocatedQuantity ??
            Math.max(
              1,
              singleProduct.soldQuantity + singleProduct.stockRemaining,
            ),
        )
      : "",
  );
  const parsedQuantity = Number(quantity);
  const quantityValid =
    !singleProduct ||
    (Number.isInteger(parsedQuantity) &&
      parsedQuantity >= Math.max(1, singleProduct.soldQuantity) &&
      (singleProduct.isPreorder ||
        parsedQuantity <=
          singleProduct.soldQuantity + singleProduct.stockRemaining));
  const cfCode = singleProduct
    ? codeEdit?.id === singleProduct.id
      ? codeEdit.value
      : singleProduct.cfCode
    : "";
  const [changeOptions, setChangeOptions] = useState(global);
  const [inheritOptions, setInheritOptions] = useState(
    !global && products.every((p) => p.optionsInherited),
  );
  const [options, setOptions] = useState<WorkspaceOption[]>(
    global
      ? workspace.promo.options
      : products.length === 1
        ? products[0]!.options
        : [],
  );
  const [shipping, setShipping] = useState({
    freeShipping: workspace.promo.freeShipping,
    shippingFeeLak: workspace.promo.shippingFeeLak,
  });
  const [preview, setPreview] = useState<WorkspacePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const candidates = useQuery({
    queryKey: [
      "workspace-options",
      workspace.id,
      products.map((p) => p.groupId).sort(),
      global,
    ],
    queryFn: async () => {
      const groups = global
        ? [undefined]
        : [...new Set(products.map((p) => p.groupId))];
      const lists = await Promise.all(
        groups.map((group) =>
          workspaceRequest<OptionCandidate[]>(
            `/${workspace.id}/options${group ? `?groupId=${encodeURIComponent(group)}` : ""}`,
          ),
        ),
      );
      return [
        ...new Map(
          lists.flat().map((o) => [o.adminOptionDefinitionId, o]),
        ).values(),
      ];
    },
  });
  const promo: WorkspacePromo = {
    ...workspace.promo,
    ...shipping,
    listDiscountPercent: price.mode === "percent" ? (price.value ?? 0) : null,
    listDiscountFixedLak: price.mode === "fixed" ? (price.value ?? 0) : null,
    options,
  };
  const action: WorkspaceCommand["action"] = global
    ? { type: "promo", promo }
    : {
        type: "products",
        ids: products.map((p) => p.id),
        ...(products.length === 1 && cfCode !== products[0]!.cfCode
          ? { cfCode }
          : {}),
        ...(changePrice ? { price } : {}),
        ...(changeOptions ? { options: inheritOptions ? null : options } : {}),
        ...(singleProduct && quantityValid
          ? { allocatedQuantity: parsedQuantity }
          : {}),
      };
  const fingerprint = JSON.stringify(action);
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
  }, [fingerprint, workspace.revision]);
  const inspect = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(
        await workspaceRequest<WorkspacePreview>(`/${workspace.id}/preview`, {
          key: crypto.randomUUID(),
          revision: workspace.revision,
          action,
        }),
      );
    } catch (error) {
      setPreviewError(workspaceErrorMessage(error));
    } finally {
      setPreviewing(false);
    }
  };
  const suggestedSale = (id: string) => {
    const base = products.find((p) => p.id === id)?.price ?? 0;
    return Math.max(
      0,
      Math.min(
        base,
        base - Math.round((base - (price.value ?? 0)) / 1000) * 1000,
      ),
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {global
              ? "ໂປຣໂມຊັນ ແລະບໍລິການເສີມທັງງານ"
              : `ຕັ້ງຄ່າ ${products.length} ລາຍການ`}
          </DialogTitle>
          <DialogDescription>
            {global
              ? "ສິນຄ້າໃໝ່ ແລະລາຍການທີ່ໃຊ້ຄ່າເລີ່ມຕົ້ນຈະໄດ້ຮັບໂປຣນີ້"
              : "ກວດລາຄາຈິງ ແລະຄວາມເຂົ້າກັນໄດ້ກ່ອນບັນທຶກ"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          <section className="space-y-3">
            <h3 className="font-semibold">ລາຄາ ແລະສ່ວນຫຼຸດ</h3>
            {!global && (
              <label
                htmlFor={"workspace-change-price"}
                className="flex min-h-11 items-center gap-2"
              >
                <Checkbox
                  id={"workspace-change-price"}
                  checked={changePrice}
                  onCheckedChange={(v) => setChangePrice(v === true)}
                />
                ປ່ຽນລາຄາ / ສ່ວນຫຼຸດ
              </label>
            )}
            {(global || changePrice) && (
              <>
                <WorkspaceSelect
                  label="ວິທີຕັ້ງລາຄາ"
                  value={price.mode}
                  onChange={(mode) =>
                    setPrice({
                      mode: mode as PriceInput["mode"],
                      value: undefined,
                    })
                  }
                  items={
                    global
                      ? [
                          ["percent", "ຫຼຸດ %"],
                          ["fixed", "ຫຼຸດເປັນກີບ"],
                          ["none", "ບໍ່ຫຼຸດລາຄາ"],
                        ]
                      : [
                          ["sale", "ກອກລາຄາຂາຍຈິງ"],
                          ["inherit", "ໃຊ້ໂປຣທັງງານ"],
                          ["none", "ບໍ່ຫຼຸດລາຄາ"],
                          ["percent", "ຫຼຸດ % ສະເພາະສິນຄ້າ"],
                          ["fixed", "ຫຼຸດເປັນກີບ ສະເພາະສິນຄ້າ"],
                        ]
                  }
                />
                {price.mode !== "inherit" && price.mode !== "none" && (
                  <label
                    htmlFor={"workspace-price-value"}
                    className="block space-y-1"
                  >
                    <span className="text-sm">
                      {price.mode === "sale"
                        ? "ລາຄາຂາຍຈິງ (ກີບ)"
                        : price.mode === "percent"
                          ? "ສ່ວນຫຼຸດ (%)"
                          : "ສ່ວນຫຼຸດ (ກີບ)"}
                    </span>
                    <Input
                      id={"workspace-price-value"}
                      type="number"
                      min={0}
                      max={price.mode === "percent" ? 100 : 999999999}
                      className="min-h-11"
                      value={price.value ?? ""}
                      onChange={(event) =>
                        setPrice({
                          ...price,
                          value:
                            event.target.value === ""
                              ? undefined
                              : Number(event.target.value),
                        })
                      }
                    />
                  </label>
                )}
              </>
            )}
            {global && (
              <>
                <label
                  htmlFor={"workspace-free-shipping"}
                  className="flex min-h-11 items-center gap-2"
                >
                  <Checkbox
                    id={"workspace-free-shipping"}
                    checked={shipping.freeShipping}
                    onCheckedChange={(v) =>
                      setShipping({ ...shipping, freeShipping: v === true })
                    }
                  />
                  ສົ່ງຟຣີ
                </label>
                <label
                  htmlFor={"workspace-shipping-fee"}
                  className="block space-y-1"
                >
                  <span className="text-sm">ຄ່າຈັດສົ່ງ (ກີບ)</span>
                  <Input
                    id={"workspace-shipping-fee"}
                    type="number"
                    min={0}
                    className="min-h-11"
                    disabled={shipping.freeShipping}
                    value={shipping.shippingFeeLak ?? ""}
                    onChange={(e) =>
                      setShipping({
                        ...shipping,
                        shippingFeeLak:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </label>
              </>
            )}
          </section>
          <section className="space-y-3">
            <h3 className="font-semibold">ບໍລິການເສີມ</h3>
            {!global && (
              <>
                <label
                  htmlFor={"workspace-change-options"}
                  className="flex min-h-11 items-center gap-2"
                >
                  <Checkbox
                    id={"workspace-change-options"}
                    checked={changeOptions}
                    onCheckedChange={(v) => setChangeOptions(v === true)}
                  />
                  ປ່ຽນບໍລິການເສີມ
                </label>
                {changeOptions && (
                  <WorkspaceSelect
                    label="ໃຊ້ບໍລິການຈາກ"
                    value={inheritOptions ? "inherit" : "custom"}
                    items={[
                      ["inherit", "ຄ່າເລີ່ມຕົ້ນທັງງານ"],
                      ["custom", "ຕັ້ງສະເພາະລາຍການນີ້"],
                    ]}
                    onChange={(v) => setInheritOptions(v === "inherit")}
                  />
                )}
              </>
            )}
            {(global || (changeOptions && !inheritOptions)) &&
              (candidates.isError ? (
                <p role="alert">ໂຫຼດບໍລິການບໍ່ສຳເລັດ</p>
              ) : (
                <OptionEditor
                  candidates={candidates.data ?? []}
                  value={options}
                  onChange={setOptions}
                />
              ))}
          </section>
        </div>
        {!global && singleProduct && (
          <div className="space-y-2">
            <p className="text-sm">
              ລາຄາປົກກະຕິ: <strong>{kip(singleProduct.price)}</strong>
            </p>
            <label htmlFor={"workspace-cf-code"} className="block space-y-1">
              <span className="text-sm">ລະຫັດ CF</span>
              <Input
                id={"workspace-cf-code"}
                className="min-h-11"
                maxLength={10}
                value={cfCode}
                onChange={(event) =>
                  singleProduct &&
                  setCodeEdit({
                    id: singleProduct.id,
                    value: event.target.value.toUpperCase(),
                  })
                }
              />
            </label>
            <label
              htmlFor={"workspace-allocated-quantity"}
              className="block space-y-1"
            >
              <span className="text-sm">ຈຳນວນເປີດຂາຍໃນ Feed / Live</span>
              <Input
                id={"workspace-allocated-quantity"}
                type="number"
                min={Math.max(1, singleProduct.soldQuantity)}
                max={
                  singleProduct.isPreorder
                    ? undefined
                    : singleProduct.soldQuantity + singleProduct.stockRemaining
                }
                step={1}
                className="min-h-11"
                value={quantity}
                aria-invalid={!quantityValid}
                onChange={(event) => setQuantity(event.target.value)}
              />
              <span className="block text-muted-foreground text-xs">
                ຮັບ CF ແລ້ວ {singleProduct.soldQuantity} · ສະຕັອກ POS ທີ່ເຫຼືອ{" "}
                {singleProduct.stockRemaining}
              </span>
              {!quantityValid && (
                <span className="block text-destructive text-xs">
                  ຈຳນວນຕ້ອງບໍ່ຕ່ຳກວ່າທີ່ຂາຍແລ້ວ ແລະບໍ່ເກີນສະຕັອກ POS
                </span>
              )}
            </label>
          </div>
        )}
        {saveError && (
          <p role="alert" className="text-destructive">
            {saveError}
          </p>
        )}
        {previewError && (
          <p role="alert" className="text-destructive">
            {previewError}
          </p>
        )}
        {preview && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="font-semibold">ກວດກາກ່ອນບັນທຶກ</p>
            <div className="max-h-64 overflow-auto">
              {preview.items.map((item) => (
                <div key={item.id} className="border-b py-2 text-sm">
                  <strong>{item.cfCode}</strong> · {kip(item.before)} →{" "}
                  {kip(item.after)} + {kip(item.options)} ={" "}
                  <strong>{kip(item.after + item.options)} / ຊິ້ນ</strong>
                  {item.error && (
                    <p className="text-destructive">
                      {workspaceErrorMessage(new Error(item.error))}
                      {item.error === "PRICE_NOT_REPRESENTABLE" &&
                        price.mode === "sale" &&
                        price.value !== undefined && (
                          <Button
                            variant="outline"
                            className="mt-2 min-h-11"
                            onClick={() => {
                              setPrice({
                                mode: "sale",
                                value: suggestedSale(item.id),
                              });
                            }}
                          >
                            ໃຊ້ລາຄາ {kip(suggestedSale(item.id))}
                          </Button>
                        )}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-muted-foreground text-sm">
              ຄ່າຈັດສົ່ງແຍກຈາກຍອດຕໍ່ຊິ້ນ · ອໍເດີເກົ່າຮັກສາລາຄາເດີມ
            </p>
          </div>
        )}
        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 bg-background py-2">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={busy}
            onClick={onClose}
          >
            ຍົກເລີກ
          </Button>
          <Button
            variant="secondary"
            className="min-h-11"
            disabled={
              previewing ||
              busy ||
              ((global || changePrice) &&
                !["inherit", "none"].includes(price.mode) &&
                price.value === undefined) ||
              !quantityValid
            }
            onClick={() => void inspect()}
          >
            {previewing ? "ກຳລັງກວດ…" : "ກວດລາຄາກ່ອນ"}
          </Button>
          <Button
            className="min-h-11"
            disabled={
              busy ||
              !quantityValid ||
              !preview ||
              preview.revision !== workspace.revision ||
              (!global && !preview.items.length) ||
              (global && preview.items.some((p) => p.error)) ||
              (preview.items.length > 0 && preview.items.every((p) => p.error))
            }
            onClick={async () => {
              if (await onSave(action)) onClose();
            }}
          >
            {busy ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກລາຍການທີ່ໃຊ້ໄດ້"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
